import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MapPin, ListOrdered, Loader2 } from "lucide-react";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";
import { toast } from "react-toastify";
import { FALLBACK_ADMISSION_CENTERS, requestAdmissionCenters } from "../../utils/admissionCenters";
import "./AddAdmission.css";
import "./AdmissionCentersSettings.css";

const AdmissionCentersSettings = () => {
    const token = decryptToken("accessToken") || "";
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const [centers, setCenters] = useState([]);
    const [usingDefaults, setUsingDefaults] = useState(false);
    const [newName, setNewName] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!token) {
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const { res, data } = await requestAdmissionCenters("GET", token);
                if (!res.ok) {
                    const hint =
                        res.status === 404
                            ? "Admission centers API returned 404. Deploy the latest backend (GET /admission/centers or /admission-centers)."
                            : data.message || `Request failed (${res.status})`;
                    throw new Error(hint);
                }
                if (!cancelled) {
                    setCenters(Array.isArray(data.centers) ? data.centers : []);
                    setUsingDefaults(Boolean(data.usingDefaults));
                }
            } catch (e) {
                if (!cancelled) {
                    toast.error(e.message || "Could not load centers");
                    setCenters([...FALLBACK_ADMISSION_CENTERS]);
                    setUsingDefaults(true);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token]);

    const addCenter = () => {
        const s = newName.trim();
        if (!s) return;
        if (centers.some((c) => c.toLowerCase() === s.toLowerCase())) {
            toast.warn("That center is already in the list");
            return;
        }
        setCenters((prev) => [...prev, s]);
        setNewName("");
        setUsingDefaults(false);
    };

    const removeAt = (index) => {
        setCenters((prev) => prev.filter((_, i) => i !== index));
    };

    const save = async () => {
        if (!token) return;
        setSaving(true);
        try {
            const { res, data } = await requestAdmissionCenters("PUT", token, { centers });
            if (!res.ok) {
                const hint =
                    res.status === 404
                        ? "Save failed: API not found (404). Deploy the latest backend."
                        : data.message || `Save failed (${res.status})`;
                throw new Error(hint);
            }
            setCenters(Array.isArray(data.centers) ? data.centers : centers);
            setUsingDefaults(Boolean(data.usingDefaults));
            toast.success(data.message || "Saved");
        } catch (e) {
            toast.error(e.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const resetToDefaults = async () => {
        if (!token) return;
        setSaving(true);
        try {
            const { res, data } = await requestAdmissionCenters("PUT", token, { centers: [] });
            if (!res.ok) {
                const hint =
                    res.status === 404
                        ? "Reset failed: API not found (404). Deploy the latest backend."
                        : data.message || `Reset failed (${res.status})`;
                throw new Error(hint);
            }
            setCenters(Array.isArray(data.centers) ? data.centers : []);
            setUsingDefaults(true);
            toast.success("Using default centers. You can customize again anytime.");
        } catch (e) {
            toast.error(e.message || "Reset failed");
        } finally {
            setSaving(false);
        }
    };

    if (!isLoggedIn?.main_id) {
        return (
            <div className="admission-form-shell">
                <div className="admission-form-shell__bg" aria-hidden />
                <div className="acs-signin-card">
                    <p>Please sign in to manage admission centers.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admission-form-shell">
            <div className="admission-form-shell__bg" aria-hidden />
            <div className="admission-form-container admission-form-container--narrow">
                <header className="admission-form-header">
                    <Link to="/admission" className="admission-form-back" aria-label="Back to admissions">
                        <ArrowLeft size={18} strokeWidth={2.25} aria-hidden />
                        Back
                    </Link>
                    <div className="admission-form-header-text">
                        <h1>Admission centers</h1>
                        <p>Control which locations appear when creating or editing admissions.</p>
                    </div>
                </header>

                <div className="admission-form-card">
                    <div className="acs-card-inner">
                        <p className="acs-lead">
                            These names power the <strong>Center</strong> field on add/edit admission and should match your
                            import spreadsheets. IDs use this label, e.g.{" "}
                            <code>ADM-KANDIVALI-2026-001</code>.
                        </p>

                        {usingDefaults && (
                            <div className="acs-notice" role="status">
                                <MapPin size={20} strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden />
                                <span>
                                    You&apos;re using the <strong>default</strong> center list. Add names below and save to
                                    replace it for your account.
                                </span>
                            </div>
                        )}

                        {loading ? (
                            <div className="acs-loading" aria-busy="true">
                                <Loader2 size={22} className="acs-spin" strokeWidth={2} aria-hidden />
                                <span>Loading centers…</span>
                            </div>
                        ) : (
                            <>
                                <h2 className="acs-section-label">
                                    <ListOrdered size={16} strokeWidth={2.25} aria-hidden />
                                    Your centers
                                </h2>

                                {centers.length > 0 ? (
                                    <ul className="acs-list">
                                        {centers.map((c, i) => (
                                            <li key={`${c}-${i}`} className="acs-list-item">
                                                <span className="acs-list-item__name">{c}</span>
                                                <button
                                                    type="button"
                                                    className="acs-list-item__remove"
                                                    onClick={() => removeAt(i)}
                                                >
                                                    Remove
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="acs-empty">
                                        No centers in this list yet. Add a name or reset to defaults.
                                    </p>
                                )}

                                <div className="acs-add-block">
                                    <h2 className="acs-section-label">
                                        <MapPin size={16} strokeWidth={2.25} aria-hidden />
                                        Add center
                                    </h2>
                                    <div className="acs-add-row">
                                        <input
                                            type="text"
                                            className="acs-input"
                                            value={newName}
                                            onChange={(e) => setNewName(e.target.value)}
                                            placeholder="e.g. Kandivali"
                                            aria-label="New center name"
                                            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCenter())}
                                        />
                                        <button type="button" className="acs-btn-add" onClick={addCenter}>
                                            Add to list
                                        </button>
                                    </div>
                                </div>

                                <div className="acs-footer-actions">
                                    <button
                                        type="button"
                                        className="acs-btn-primary"
                                        disabled={saving}
                                        onClick={save}
                                    >
                                        {saving ? "Saving…" : "Save centers"}
                                    </button>
                                    <button
                                        type="button"
                                        className="acs-btn-secondary"
                                        disabled={saving}
                                        onClick={resetToDefaults}
                                    >
                                        Reset to defaults
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdmissionCentersSettings;
